"use client"
import Image from "next/image"

import { CardBasic } from "@/components/ui-library-new/card/CardBasic"
import { IconCircle } from "@/components/ui-library-new/icon/IconCircle"
import { SearchableSelect } from "@/components/ui-library-new/select/SearchableSelect"
import Richtext from "@/components/ui-library/richtext/Richtext"
import { BlokProps } from "@/helpers/StoryblokHelpers"
import type { VaccinationPrescriptionSmsSectionStoryblok } from "@/types/component-types"
import { SbBlokData, storyblokEditable } from "@storyblok/react/rsc"
import { FormikProps, useFormik } from "formik"
import * as Yup from "yup"

import { Button } from "@/components/ui-library/button/Button"
import ConfirmDialog from "@/components/ui-library/dialog/ConfirmDialog"
import InputField from "@/components/ui-library/form/InputField"
import { MESSAGE_TYPES, MOBILE_NUMBER_PATTERN, normaliseMobileNumber } from "@/consts/prescription-sms"
import SpinnerIcon from "@/layout/icons/SpinnerIcon"
import { sendPrescriptionSms } from "@/lib/sendPrescriptionSms"
import { cn } from "@/utils/utils"
import { useState } from "react"
import { FiSmartphone } from "react-icons/fi"

const gridClasses =
  "mx-auto mt-5 grid w-full gap-4 lg:mt-[17px] lg:auto-rows-fr lg:grid-cols-3 lg:[&>*:first-child]:col-span-2"

type SelectOption = {
  label: string
  value: string
}

type FormValues = {
  mobileNumber: string
  option: SelectOption
}

type Message = {
  title: string
  content: string
  error: boolean
}

const options: SelectOption[] = [
  {
    label: "Välj typ av sms",
    value: ""
  },
  {
    label: "Sök vård",
    value: MESSAGE_TYPES.find(t => t === "seek_healthcare") as string
  },
  {
    label: "Receptförnyelse",
    value: MESSAGE_TYPES.find(t => t === "renew_prescription") as string
  }
]
export const VaccinationPrescriptionSmsSection = ({ blok }: BlokProps<VaccinationPrescriptionSmsSectionStoryblok>) => {
  const { items } = blok
  const [openDialog, setOpenDialog] = useState<boolean>(false)
  const [message, setMessage] = useState<Message>({ title: "", content: "", error: false })

  const initialFormData: FormValues = {
    mobileNumber: "",
    option: options[0]
  }

  const formik = useFormik({
    initialValues: initialFormData,
    validateOnMount: true,
    validationSchema: Yup.object().shape({
      mobileNumber: Yup.string()
        .transform(value => (typeof value === "string" ? normaliseMobileNumber(value) : value))
        // `excludeEmptyString` so `required()` owns the empty case. Without it both tests fail on ""
        // and Formik keeps the first error per field, so an untouched-then-blurred field complained
        // about the format rather than asking to be filled in.
        .matches(MOBILE_NUMBER_PATTERN, {
          message: "Ange ett giltigt mobilnummer, t.ex. 0701234567 eller +447700900123",
          excludeEmptyString: true
        })
        .required("*Fältet är obligatoriskt"),
      option: Yup.object().shape({
        label: Yup.string().required("*Fältet är obligatoriskt"),
        value: Yup.string()
          .not([options[0].value], "*Välj ett giltigt alternativ")
          .required("*Välj ett giltigt alternativ")
      })
    }),
    onSubmit: async values => {
      try {
        const {
          mobileNumber,
          option: { value: messageType }
        } = values
        const result = await sendPrescriptionSms(mobileNumber, messageType)

        if (result.success) {
          setMessage({ title: "Tack!", content: "Ditt meddelande har skickats.", error: false })
          formik.resetForm()
        } else {
          setMessage({
            title: "Ett fel inträffade",
            content: result.error ?? "Meddelandet kunde inte skickas prova senare",
            error: true
          })
        }
      } catch (error) {
        console.error(error)
        setMessage({ title: "Ett fel inträffade", content: "Meddelandet kunde inte skickas prova senare", error: true })
      } finally {
        handleDialogState(true)
      }
    }
  })

  const handleDialogState = (open: boolean) => {
    setOpenDialog(open)
  }

  if (!items || items.length === 0) return <></>

  return (
    <div {...storyblokEditable(blok as SbBlokData)} className={cn("-mt-8 xl:-mt-16", gridClasses)}>
      <SendSmsToCustomerCard form={formik} options={options} />
      {items.map((item, index) => (
        <CardBasic className="justify-normal shadow-elevation-1" key={index}>
          {/* Icon */}
          <div className="flex items-center justify-start gap-2 align-middle">
            <IconCircle
              icon="ClinicIcon"
              className="h-10 w-10 lg:h-14 lg:w-14 2xl:h-[70px] 2xl:w-[70px]"
              iconClassName="h-5 w-5 lg:h-7 lg:w-7 2xl:h-8 2xl:w-8"
            />
            <h2 className="m-0 text-[15px] font-semibold text-neutral-90 2xl:text-xl">{item.title}</h2>
          </div>

          <div className="mb-3 flex items-center gap-3 py-0 text-sm font-semibold 2xl:mb-2 2xl:gap-4 2xl:py-2">
            {/* Name & chips */}
            <div className="min-w-0 flex-col items-start gap-1 2xl:gap-2">
              <Richtext
                className="m-0 mt-5 tracking-tight [&_p]:text-sm lg:[&_p]:text-[16px]"
                key={`vaccination_prescription_sms_content-${index}`}
                content={item.content}
              />
            </div>
          </div>
        </CardBasic>
      ))}

      <ConfirmDialog
        title={message.title}
        message={message.content}
        error={message.error}
        open={openDialog}
        onClose={() => handleDialogState(false)}
      />
    </div>
  )
}

const SendSmsToCustomerCard = ({ form, options }: { form: FormikProps<FormValues>; options: SelectOption[] }) => {
  return (
    <CardBasic className="p-8 shadow-elevation-2" variant="softElevated" key="send-sms-to-customer-card">
      <div className="mb-3 flex items-end gap-3 text-sm font-semibold 2xl:mb-2 2xl:gap-4 2xl:py-2">
        {/* Sms image */}
        <Image
          src="/sms-to-customer.svg"
          alt=""
          aria-hidden
          width={120}
          height={134}
          className="animate-duration-800 hidden h-auto w-[120px] shrink-0 animate-fadeIn xl:block"
        />

        <div className="flex w-full min-w-0 flex-col gap-3">
          <h3 className="mb-4 flex gap-3 font-semibold text-neutral-90 xl:m-0">
            <FiSmartphone className="block h-auto w-8 animate-riseIn justify-center self-center align-middle text-primary-50 xl:hidden" />
            Skicka sms
          </h3>
          <p className="m-0 font-normal">
            Fyll i kundens mobilnummer för att skicka en länk om recept eller för vårdärende hos{" "}
            <strong className="text-primary-50">
              <a className="text-primary-50 hover:underline" href="https://www.doktor.se">
                Doktor.se
              </a>
            </strong>
          </p>
          <div className="flex w-full flex-col justify-between gap-2 xl:flex-row">
            <label className="w-full" htmlFor={`customer-mobile-number`}>
              <InputField
                disabled={form.isSubmitting}
                id={`customer-mobile-number`}
                name="mobileNumber"
                className="px-2 py-3 font-text placeholder:pl-2 placeholder:text-sm"
                placeholder="Kundens mobilnummer"
                value={form.values.mobileNumber}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
              />
              {form.touched["mobileNumber"] && form.errors["mobileNumber"] ? (
                <div className="text-warning-50">
                  {typeof form.errors["mobileNumber"] === "string" ? form.errors["mobileNumber"] : null}
                </div>
              ) : null}
            </label>
            <label className="w-full">
              <SearchableSelect
                disabled={form.isSubmitting}
                searchable={false}
                classNameButton="h-12 text-sm font-tight"
                placeholder="Välj typ av sms"
                value={form.values.option.value}
                options={options}
                onChange={value => {
                  form.setFieldTouched("option", true, false)
                  form.setFieldValue("option", options.find(item => item.value === value) ?? options[0], true)
                }}
              ></SearchableSelect>
              {form.touched.option && form.errors.option ? (
                <span className="text-warning-50">
                  {typeof form.errors.option === "string" ? form.errors.option : form.errors.option.value}
                </span>
              ) : null}
            </label>
            <Button
              className="flex w-full gap-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 xl:w-max xl:max-w-4 xl:self-start"
              disabled={!form.isValid || form.isSubmitting}
              onClick={() => form.submitForm()}
            >
              {form.isSubmitting && <SpinnerIcon className="animate-spin" width={20}></SpinnerIcon>}Skicka
            </Button>
          </div>
        </div>
      </div>
    </CardBasic>
  )
}

export default VaccinationPrescriptionSmsSection
